<template>
  <VDialogV3
    id="offer-edit-dialog"
    position="center"
    :entity="localOffer"
    width="1130px"
    ref="dialog"

    @submit="onSubmit"
  >
    <template #header>
      <div class="title">
        {{ localOffer?.toString() }}
      </div>
      <ul v-if="localOffer" class="buyer-description">
        <li :title="localOffer.buyer?.name">
          <span class="label grey">Покупатель:</span>
          <span class="value ellipsis">{{ localOffer.buyer?.name }}</span>
        </li>
        <li :title="localOffer.buyerStore">
          <span class="label grey">Склад покупателя:</span>
          <span class="value ellipsis">{{ localOffer.buyerStore }}</span>
        </li>
      </ul>
    </template>

    <OfferEditDialogNavigation
      :sections="navSections"
      scroll-root-selector="#offer-edit-dialog .main"
    />

    <section id="product">
      <header>Товар</header>

      <VFormItem layout="horizontal">
        <VSelectV2
          v-model="localOffer.productCategory"
          label="Товар"
          label-width="220px"
          layout="horizontal"
          hide-clear-btn
          :data="productCategoryLoader"
          required
        >
          <template #option="{ option }">
            <div>
              <VIcon
                v-if="option.isBookmarked"
                material="favorite"
              />
              <VIcon
                v-else
                material="favorite_border"
              />
              {{ option.name }}
            </div>
          </template>
        </VSelectV2>
        <VLink
          :disabled="!localOffer.productCategory"
          :title="localOffer.productCategory?.isBookmarked ? 'Исключить из избранного' : 'Добавить в избранное'"
          :material="localOffer.productCategory?.isBookmarked ? 'favorite' : 'favorite_border'"
          @click="toggleCategoryBookmark"
        />
      </VFormItem>
      <VSwitch
        v-model="localOffer.isSamples"
        theme="slider"
        layout="horizontal"
        label-width="220px"
        label="Образцы"
      />
    </section>

    <section id="buyer">
      <header>Покупатель</header>

      <VSelectV2
        v-model="localOffer.buyer"
        disabled
        layout="horizontal"
        label-width="220px"
        label="Покупатель"
      />

      <VSelectV2
        v-model="localOffer.buyerStore"
        disabled
        layout="horizontal"
        label-width="220px"
        label="Склад покупателя"
      />
    </section>

    <section
      id="characteristics"
      v-if="$core.utils.isNotEmptyArray(localOffer.characteristics)"
    >
      <header>Характеристики</header>

      <OfferCharacteristicFormItem
        v-for="(characteristic, key) in visibleCharacteristics"
        :key="key"
        layout="horizontal"
        label-width="220px"
        :offer-characteristic="characteristic"
        :product="instanceOfAbstractDemand?.product"
      />
    </section>

    <section id="transportation">
      <header>Условия перевозки</header>

      <VTemperatureRangeInput
        v-model="localOffer.storeTemperatureRange"
        :required="true"
        :with-asterisk="true"
        label="Температурный режим перевозки, °C"
        layout="horizontal"
      />

      <VTextarea
        v-model="localOffer.storeRulesText"
        label="Особые условия перевозки"
        layout="horizontal"
        label-width="220px"
        :max-length="300"
      />
    </section>

    <section id="offer-requisites">
      <header>Параметры предложения</header>

      <VButtonGroup>
        <VButton
          :theme="localOffer.isPublished ? 'primary' : 'default'"
          @click="localOffer.isPublished = true"
        >
          Активно
        </VButton>
        <VButton
          :theme="!localOffer.isPublished ? 'primary' : 'default'"
          @click="localOffer.isPublished = false"
        >
          Не активно
        </VButton>
      </VButtonGroup>

      <VInput
        v-model="localOffer.name"
        required
        layout="horizontal"
        label-width="220px"
        label="Наименование товара в ИС поставщика"
        :max-length="175"
      >
        <template #helper-text>
          Наименование будет отображаться в отгрузочных документах, на транспортном ярлыке, в
          Декларации соответствия и сертификатах анализа
        </template>
      </VInput>

      <VInput
        v-model="localOffer.sellersCode"
        required
        layout="horizontal"
        label-width="220px"
        label="Артикул"
        :max-length="60"
      >
        <template #helper-text>
          Артикул в вашей системе учета. Будет использоваться для интеграции (обновление
          остатков, цены)
        </template>
      </VInput>

      <VSelectV2
        v-model="localOffer.manufacturer"
        required
        searchable
        layout="horizontal"
        hide-clear-btn
        :data="({ pattern }) => ManufacturersService.getList({ pattern })"
        label-width="220px"
        label="Производитель"
        with-asterisk
        :class="{ attention: localOffer.getManufacturerMismatch(desiredManufacturers) }"
        :data-desired="desiredManufacturersLabel"
      >
        <template #helper-text>
          Выберите из списка производителей, при отсутствии необходимого отправьте заявку на
          добавление по адресу nazarenko.dv@sibvaleo.com
        </template>
      </VSelectV2>
    </section>

    <section id="delivery">
      <header>Доставка</header>

      <VGroupButtonSelector
        v-model="localOffer.deliveryType"
        value-key="id"
        label-key="name"
        :options="deliveryTypeSellerOptions"
      />

      <VDateRangeInputNew
        required
        v-model="localOffer.shipmentPeriod"
        @update:modelValue="onInputShipmentPeriod"
        :validate-method="validateDate"
        label="Интервал отгрузки"
        layout="horizontal"
        label-width="220px"
        with-asterisk
        :class="{ attention: shipmentPeriodMismatch }"
        :data-desired="desiredShipmentPeriodLabel"
      >
        <template #helper-text>
          <template v-if="demand && localOffer.deliveryDays">
            Рекомендуем установить дату от
            {{ $core.utils.date.format(demandRecommendedPublicationDate) }}
            для полного удовлетворения потребности покупателя
          </template>
        </template>
      </VDateRangeInputNew>

      <VInputNumberRus
        v-model="localOffer.deliveryDays"
        label-width="220px"
        layout="horizontal"
        label="Срок доставки до покупателя"
        :disabled="localOffer.deliveryType.id === 'byETP'"
        :precision="0"
        required
        :min="0"
        :max="365"
        :unit="localOffer.deliveryDays ? `${$core.utils.regional.ru.pluralize(localOffer.deliveryDays, 'день', 'дня', 'дней') }` : undefined"
      >
        <template #helper-text>
          <template v-if="localOffer.deliveryType.id === 'byETP'">
            Доставкой до покупателя занимается Платформа.
          </template>
        </template>
      </VInputNumberRus>

      <VSelectV2
        v-model="localOffer.sellerStore"
        label="Склад"
        layout="horizontal"
        label-width="220px"
        hide-clear-btn
        :data="localOffer.seller.stores"
        :validate-method="store => store?.validateTransportZone()"
        required
      >
        <template #helper-text>
          <template v-if="localOffer?.deliveryType?.id === 'byETP'">
            Выберите склад, откуда забирать груз
          </template>
          <template v-if="localOffer?.deliveryType?.id === 'bySeller'">
            Выберите склад, откуда будете отгружать груз
          </template>
          <br>

          <VHelperText theme="blue-flat">
            <template
              v-if="localOffer?.productCategory && localOffer?.deliveryType?.id === 'byETP'">
              Подробнее о компенсации доставки в

              <VLink @click="openContract('seller')">договоре поставки</VLink>
            </template>
            <template v-else>
              Вы обязуетесь доставить товар по адресу: {{ desiredBuyerStore }}
            </template>
          </VHelperText>
        </template>
      </VSelectV2>

      <VSelectV2
        v-model="localOffer.transportationRoute"
        label="Маршрут перевозки"
        layout="horizontal"
        label-width="220px"
        hide-clear-btn
        :data="transportRouteSource"
        disabled
      />
    </section>

    <section id="publication-period">
      <header>Публикация</header>

      <VCheckbox
        v-model="isPublicationPeriodTheSameShipmentPeriod"
      >
        Использовать период поставки
      </VCheckbox>
      <VDateRangeInputNew
        :disabled="isPublicationPeriodTheSameShipmentPeriod"
        label="Интервал публикации"
        required
        layout="horizontal"
        label-width="220px"
        v-model="localOffer.publicationPeriod"
        :validate-method="validateDate"
      >
        <template #helper-text>
          {{ publicationPeriodHelperText }}
        </template>
      </VDateRangeInputNew>
    </section>

    <section id="offer-terms">
      <header>Коммерческое предложение</header>

      <VSelectV2
        v-model="localOffer.sellerCompany"
        label="Юр. лицо"
        layout="horizontal"
        label-width="220px"
        hide-clear-btn
        :data="sellerCompanyLoader"
        required
        :class="{ attention: localOffer.getSellerCompanyMismatch(desiredSellerCompanies) }"
        :data-desired="desiredSellerCompaniesLabel"
      >
        <template #helper-text>
          <div>
            Выберите юридическое лицо от имени которого будет заключен договор
          </div>
        </template>
      </VSelectV2>

      <VSelectV2
        v-model="localOffer.unit"
        required
        layout="horizontal"
        :data="UnitsService.units"
        hide-clear-btn
        label-width="220px"
        label="Единица измерения"
        :class="{ attention: instanceOfAbstractDemand && localOffer.getUnitMismatch(instanceOfAbstractDemand.unit) }"
        :data-desired="instanceOfAbstractDemand && desired('Желаемая ед. измерения', instanceOfAbstractDemand.unit.name)"
      />

      <VInputNumberRusV2
        v-model="localOffer.qtyAvailable"
        required
        layout="horizontal"
        :type="Qty"
        :max="10000000000"
        :min="0"
        :validate-method="validateQty"
        :unit="localOffer.unit"
        label-width="220px"
        label="Количество"
        with-asterisk
        :class="{ attention: instanceOfAbstractDemand && localOffer.getQtyMismatch(instanceOfAbstractDemand.qtyDesired) }"
        :data-desired="instanceOfAbstractDemand && desired('Желаемое кол-во', instanceOfAbstractDemand.qtyDesired, instanceOfAbstractDemand.unit.name)"
      >
        <template #helper-text>
          Количество товара, которое Вы хотите продавать на этих условиях
        </template>
      </VInputNumberRusV2>

      <VSelectV2
        v-model="localOffer.package"
        required
        searchable
        layout="horizontal"
        :data="({ pattern }) => PackagesService.getList({ pattern })"
        hide-clear-btn
        label-width="220px"
        label="Упаковка"
      >
        <template #option="{ option }">
          <div>
            <b>{{ option.code }}</b>&nbsp;
            <span>{{ option.name }}</span>
          </div>
        </template>
      </VSelectV2>

      <VInputNumberRusV2
        v-model="localOffer.packageVolume"
        required
        layout="horizontal"
        label-width="220px"
        label="Фасовка"
        :type="Qty"
        :max="1000"
        :unit="localOffer.unit?.name"
        @input="setWeight"
        :validate-method="validatePackageVolume"
      />

      <VDateInput
        v-model="localOffer.productExpirationDate"
        required
        layout="horizontal"
        :show-months="2"
        :validate-method="validateDate"
        with-asterisk
        label-width="220px"
        label="Партия годна до"
        :class="{ attention: instanceOfAbstractDemand?.product?.hasExpirationRequirement && localOffer.getExpirationDateMismatch(instanceOfAbstractDemand.product.requiredExpirationDate) }"
        :data-desired="instanceOfAbstractDemand && desired('Минимальный срок годности', instanceOfAbstractDemand.product.expirationDays?.name)"
      />
    </section>

    <PackageCharacteristics
      id="package-characteristics"
      :order-option="localOffer"
      layout="horizontal"
      required
    />
    <section>
      <VFormItem
        with-asterisk
        label="Цена"
        layout="horizontal"
        label-width="220px"
        ratio="1/1"
        class="price"
      >
        <VInputNumberRusV2
          class="price-seller"
          v-model="localOffer.priceSeller"
          :type="Price"
          required
          layout="horizontal"
          :max="1000000000000000"
          :min="0.001"
        />

        <VSelectV2
          v-model="currencyCode"
          :data="$core.currencies.currencies"
          required
          hide-clear-btn
        />

        <template v-if="localOffer.sellerCompany?.isVatPayer">
          <span>НДС</span>
          <VSelectV2
            v-model="localOffer.vatRateSeller"
            required
            :data="$core.dictionaries.vatRates"
            hide-clear-btn
          />
          <VSelectV2
            class="vat-include"
            v-model="isVatIncludedSeller"
            required
            :data="$core.dictionaries.vatInclusionOptions"
            hide-clear-btn
          />
        </template>

        <template #helper-text>
          <VHelperText theme="blue-flat">
            <div>
              Цена, по которой Вы готовы продать партию товара от одной упаковки
              <b>{{ localOffer.priceFullSeller?.format(localOffer.offerCurrencyCode) }}</b>
            </div>

            <div>
              В реестре предложений поставщиков цена вашего предложения составит
              <b>{{ localOffer.priceFullBuyer?.format(localOffer.offerCurrencyCode) }} </b>
              <template
                v-if="localOffer.deliveryType.id === 'byETP' && localOffer.deliveryPricePerUnit">
                с учетом стоимости доставки
                <b> {{ localOffer.deliveryPricePerUnit?.format(localOffer.offerCurrencyCode) }} </b>
              </template>
            </div>
          </VHelperText>
        </template>
      </VFormItem>
    </section>

    <PaymentTermEditorCompact
      id="payment-terms"
      layout="horizontal"
      :entity="localOffer"
    />

    <section>
      <VTextarea
        v-model="localOffer.description"
        label="Описание"
        layout="horizontal"
      >
        <template #helper-text>
          Внесите дополнительные данные на ваше усмотрение - недостающую характеристику товара
          и
          её параметр или важную особенность товара, которая выделяет ваш товар из всех
          предложений
        </template>
      </VTextarea>
    </section>

    <section id="preorder">
      <header>Отгрузка по предзаказу</header>

      <VButtonGroup>
        <VButton
          :theme="localOffer.isAcceptingPreorder ? 'default' : 'primary'"
          @click="localOffer.isAcceptingPreorder = false"
        >
          Не отгружаю по предзаказу
        </VButton>
        <VButton
          :theme="localOffer.isAcceptingPreorder ? 'primary' : 'default'"
          @click="localOffer.isAcceptingPreorder = true"
        >
          Отгружать по предзаказу
        </VButton>
      </VButtonGroup>

      <VHelperText theme="blue-flat">
        Этим пунктом вы подтверждаете, что покупатель сможет зарезервировать у вас этот товар и
        вывозить по графику выбранными партиями к любой желаемой дате поставке. Оплата будет
        производиться частями каждый раз по факту доставки выбранной партии согласно периоду
        отсрочки
      </VHelperText>

      <VInputNumberRusV2
        ref="preorderMinQtyInput"
        v-model="localOffer.preorderMinQty"
        :type="Qty"
        label-width="220px"
        layout="horizontal"
        label="Минимальное количество по предзаказу"
        :disabled="!localOffer.isAcceptingPreorder"
        :precision="3"
        :unit="localOffer.unit?.name"
        @validate-method="validateQty"
      >
        <template #helper-text>
          Минимальное количество товара, которое вы готовы поставить по предзаказу
        </template>
      </VInputNumberRusV2>

      <VInputNumberRusV2
        ref="preorderMonthlyReserveInput"
        v-model="localOffer.preorderMonthlyReserve"
        :type="Qty"
        label-width="220px"
        layout="horizontal"
        :disabled="!localOffer.isAcceptingPreorder"
        :precision="3"
        @validate-method="validateQty"
        label="Минимальный страховой запас"
        :unit="localOffer.unit?.name"
        :class="{ attention: instanceOfAbstractDemand && localOffer.getPreorderReserveMismatch(instanceOfAbstractDemand.preorderMonthlyReserve) }"
        :data-desired="instanceOfAbstractDemand?.preorderMonthlyReserve && desired('Желаемый страховой запас', instanceOfAbstractDemand.preorderMonthlyReserve, instanceOfAbstractDemand.unit.name)"
      >
        <template #helper-text>
          Количество товара, которое вы обязуетесь постоянно поддерживать на складе
          дополнительно (но не выше остатка по всему Предзаказу) к заказанным партиям
          покупателем для быстрой дополнительной отгрузки в течение 3-5 дней. Если вы готовы
          сразу хранить весь товар по Предзаказу, то укажите его
        </template>
      </VInputNumberRusV2>
    </section>

    <template #footer>
      <div class="top-footer">
        <VCheckbox class="agreement" v-model="sellerAgreementModel">
          Отправляя форму, я принимаю условия
          <VLink @click.stop="openContract('seller')">договора продавца</VLink>
          на платформе
        </VCheckbox>

        <div
          v-if="instanceOfAbstractDemand && $core.utils.isNotEmptyArray(localOffer.getMismatches(instanceOfAbstractDemand))"
          class="warning-wrapper"
        >
          <div class="attention-dot"/>
          - имеется расхождение
        </div>
      </div>

      <div class="actions">
        <VButton
          theme="primary"
          :disabled="submitDisabled !== null || isLoading"
          @click="onSubmitButtonClicked"
          :title="submitDisabled"
        >
          Сохранить
        </VButton>

        <VButton
          theme="primary"
          @click="OffersService.deleteWithConfirm(localOffer.id)"
          v-if="localOffer.isExisting">
          Удалить предложение
        </VButton>

        <VButton
          v-if="localOffer.isExisting"
          @click="onAddDoubleOffer">
          Создать новое копированием
        </VButton>

        <VButton @click="$core.dialogs.OfferEditDialog.hide()">
          Отменить
        </VButton>
      </div>
    </template>
  </VDialogV3>
</template>

<style lang="scss" src="./OfferEditDialog.scss"/>

<script lang="ts">

import DateSimple from "@/core/types/DateSimple";
import DateRange from '@/core/types/DateRange';
import ProductCategoriesService from '@/domains/ProductCategories/ProductCategoriesService';
import Offer from '@/domains/Offers/Offer';
import ProductsService from '@/domains/Products/ProductsService';
import OffersService from "@/domains/Offers/OffersService";
import Tender from "@/domains/Tenders/Tender";
import Demand from "@/domains/Demand/Demand"
import BookmarksService from "@/domains/Bookmark/BookmarksService";
import ContractsPublicService from "@/domains/Contracts/ContractsPublicService";
import ManufacturersService from "@/domains/Manufacturers/ManufacturersService";
import CompaniesService from "@/domains/Companies/CompaniesService";
import PackagesService from "@/domains/Packages/PackagesService";
import UnitsService from "@/domains/Units/UnitsService";
import TransportRoutesService from "@/domains/TransportRoutes/TransportRoutesService";
import type Manufacturer from "@/domains/Manufacturers/Manufacturer";
import type Company from "@/domains/Companies/Company";
import type Product from "@/domains/Products/Product";
import SelectLoader from "@/lib/components/VSelectV2/SelectLoader";

import PaymentTermEditorCompact, {
  HasPaymentTerm
} from '@/domains/PaymentTerms/components/PaymentTermEditorCompact/PaymentTermEditorCompact.vue';
import VHelperText from '@/lib/components/VHelperText/VHelperText.vue';
import VGroupButtonSelector from '@/components/VGroupButtonSelector/VGroupButtonSelector.vue';
import OfferCharacteristicFormItem
  from '@/domains/Offers/components/OfferCharacteristicFormItem/OfferCharacteristicFormItem.vue';
import PackageCharacteristics
  from '@/domains/Offers/components/PackageCharacteristics/PackageCharacteristics.vue';
import OfferEditDialogNavigation
  from '@/domains/Offers/components/OfferEditDialog/OfferEditDialogNavigation.vue';
import OfferCharacteristic from "@/domains/Offers/OfferCharacteristic";

import Qty from "@/core/types/Qty";
import Volume from "@/core/types/Volume";
import Price from "@/core/types/Price";
import Weight from "@/core/types/Weight";
import Numeric from "@/core/types/Numeric";

export default defineComponent({
  name: 'OfferEditDialog',

  components: {
    OfferCharacteristicFormItem,
    OfferEditDialogNavigation,
    PackageCharacteristics,
    VGroupButtonSelector,
    PaymentTermEditorCompact,
    VHelperText,
  },

  props: {
    /** ID предложения — передаётся диалоговой системой (роутер/deep link). */
    id: {
      type: Number,
      default: null,
    },
    /** Предложение (объект из реестра) или его id.
     *  init разрулит: число — загрузит, объект — склонирует. */
    offer: {
      type: Offer as PropType<Offer>,
      default: null,
    },

    /** ID категории */
    categoryId: {
      type: Number,
      default: null,
    },

    tender: {
      type: Tender as PropType<Tender>,
      default: null,
    },

    demand: {
      type: Demand as PropType<Demand>,
      default: null,
    }
  },


  setup() {
    return {
      ProductCategoriesService,
      ProductsService,
      OffersService,
      OfferCharacteristic,
      ManufacturersService,
      PackagesService,
      UnitsService,
      Qty,
      Volume,
      Price,
      Tender
    }
  },

  data() {
    /**
     * Офер уже существует. Определяется по пропам: data() отрабатывает до
     * init(), localOffer в этот момент ещё undefined — опираться на него
     * нельзя, признак всегда сводился бы к «создание».
     */
    const isExistingOffer = this.$core.utils.isNullOrUndefined(this.offer?.id ?? this.id) === false;

    /**
     * Категория товара. Все три источника — categoryId, тендер и потребность —
     * это пропы: известны на момент создания загрузчика и за жизнь диалога не
     * меняются, поэтому вилка здесь, а не в зависимостях.
     *
     * Категория из тендера/потребности приходит половинчатой (без
     * allowedUnits, наценок и отсрочки), поэтому даже когда выбирать не из
     * чего, за полной версией всё равно нужен запрос — это и делает загрузчик.
     */
    const desiredProductCategoryId = this.categoryId
      ?? this.tender?.productCategory?.id
      ?? this.demand?.productCategory?.id
      ?? null;


    /** ------- productCategoryLoader ------- **/

    let productCategoryLoader: SelectLoader;

    if (desiredProductCategoryId === null) {
      productCategoryLoader = this.$core.loader.select(({pattern}) =>
        ProductCategoriesService.search({ pattern, isLeaf: true })
      ).pinOptions(
        ProductCategoriesService.search({
          partnerId: this.$core.currentPartner.id,
          isBookmarked: true,
        })
      )
    }
    else {
      productCategoryLoader = this.$core.loader.select(async () => [
        await ProductCategoriesService.get(desiredProductCategoryId, { requireAllowedUnits: true }),
      ])
    }

    productCategoryLoader.preloadOnMount(isExistingOffer === false)
    productCategoryLoader.preloadOnFocus(isExistingOffer)


    /** ------- sellerCompanyLoader ------- **/

    /**
     * Юр. лица поставщика. Когда офер создают из тендера или потребности,
     * желаемые юрлица известны из разрешений на поставку этого товара.
     * Разрешений может быть несколько — по одному на юрлицо, — поэтому
     * дефолт подставляется только когда юрлицо в них ровно одно: выбирать
     * за пользователя из нескольких равноправных вариантов неправильно.
     */
    const desiredProduct: Product | null = this.tender?.product ?? this.demand?.product ?? null;

    let desiredSellerCompanies: Company[] = [];

    if (desiredProduct !== null) {
      desiredSellerCompanies = desiredProduct.getDesiredSellerCompaniesByPartner(this.$core.currentPartner);
    }

    let sellerCompanyLoader = this.$core.loader.select<Offer>(({seller}) =>
      CompaniesService.getList({ partnerId: seller.id })
    )
      .dependsOn({
        seller: () => this.localOffer?.seller ?? null,
      })
      .preloadOnMount(isExistingOffer === false);

    if (desiredSellerCompanies.length === 1) {
      const desiredSellerCompany = desiredSellerCompanies[0];

      sellerCompanyLoader = sellerCompanyLoader.default(
        (options: any[]) => options.find(option => option.id === desiredSellerCompany.id),
      );
    }

    /** ------- transportRouteSource ------- **/

    /**
     * Маршрут выводится из транспортных зон складов, поэтому грузится
     * источником: смена любого из складов сама перезагружает список.
     *
     * Маршрут всегда один — список из одного элемента, который VSelectV2
     * выберет сам. Пустой список означает, что маршрута нет.
     */
    const transportRouteSource = this.$core.loader.select<Offer>(async ({sellerStore, buyerStore}) => {
      if (sellerStore.transportZone === null || buyerStore.transportZone === null) {
        return [];
      }
      else {
        const transportationRoute = await TransportRoutesService.getByZones(
          sellerStore.transportZone.id,
          buyerStore.transportZone.id,
        );

        if (transportationRoute !== null && transportationRoute.isDeleted === false) {
          return [transportationRoute];
        }
        else {
          return [];
        }
      }
    })
      .dependsOn({
        /** localOffer появляется только после init — до него зависимости
         *  пустые, источник сам себя отключает и запрос не шлёт. */
        sellerStore: () => this.localOffer?.sellerStore ?? null,
        buyerStore:  () => this.localOffer?.buyerStore ?? null,
      })
      .default((options: any[], { sellerStore, buyerStore }) => {
        // Если складов нет, то соответственно и дефолтить нечего
        if (this.$core.utils.isNullOrUndefined(sellerStore)) return undefined;
        if (this.$core.utils.isNullOrUndefined(buyerStore)) return undefined;

        /** Пустой список — осознанное отсутствие значения. Возвращаем null,
         *  чтобы контрол эмитнул input: без него форма не узнает, что
         *  маршрута нет. */
        if (this.$core.utils.isEmptyArray(options) === true) {
          return null;
        }
        else {
          return options[0];
        }
      })
      .preloadOnMount(isExistingOffer === false);

    return {
      /** Предложение ещё не загружено — вопрос не задавался, потому undefined. */
      localOffer: undefined as unknown as Offer & HasPaymentTerm,
      deleteApproveVisible: false,
      sellerAgreementModel: ContractsPublicService.isTypeAccepted('seller'),
      isLoading: false,

      productCategoryLoader,
      sellerCompanyLoader,
      transportRouteSource,

      /** Разделы для навигации: id секции в шаблоне → подпись пункта. */
      navSections: [
        {id: 'product', label: 'Товар'},
        {id: 'buyer', label: 'Покупатель'},
        {id: 'characteristics', label: 'Характеристики'},
        {id: 'transportation', label: 'Условия перевозки'},
        {id: 'offer-requisites', label: 'Параметры предложения'},
        {id: 'delivery', label: 'Доставка'},
        {id: 'publication-period', label: 'Публикация'},
        {id: 'offer-terms', label: 'Коммерческое предложение'},
        {id: 'package-characteristics', label: 'Параметры упаковки'},
        {id: 'payment-terms', label: 'Условия оплаты'},
        {id: 'preorder', label: 'Отгрузка по предзаказу'},
      ],
    };
  },

  async mounted() {
    await this.init();
  },


  computed: {
    deliveryTypeSellerOptions() {
      return this.$core.dictionaries.deliveryTypes.filter(option => [
        'byETP', 'bySeller'
      ].includes(option.id));
    },

    /** Дата, к которой поставщику стоит опубликовать предложение, чтобы успеть к сроку покупателя. */
    demandRecommendedPublicationDate(): Date | null {
      /** Без потребности рекомендовать дату не от чего */
      if (this.demand === null) {
        return null;
      }

      /** Без срока доставки не от чего отсчитывать назад */
      if (this.localOffer.deliveryDays === null) {
        return null;
      }

      /** Дата, к которой покупателю нужен товар: для предзаказа — начало периода
       *  выборки, для обычной потребности — желаемая дата поставки. */
      let demandReceiptDate;

      if (this.demand.isPreorderNeeded === true) {
        demandReceiptDate = this.$core.utils.date.cloneDate(this.demand.preorderShipmentPeriod!.start);
      } else {
        demandReceiptDate = this.$core.utils.date.cloneDate(this.demand.desiredReceiptDate);
      }

      /** Отсчитываем назад срок доставки — получаем дату публикации. */
      demandReceiptDate.setDate(demandReceiptDate.getDate() - this.localOffer.deliveryDays);

      return demandReceiptDate;
    },

    /** Образец для сравнения: потребность или тендер. null — карточку открыли без образца. */
    instanceOfAbstractDemand(): Tender | Demand | null {
      return this.demand || this.tender || null;
    },

    desiredShipmentPeriodLabel(): string | null {
      if (this.instanceOfAbstractDemand === null) {
        return null;
      }
      else if (
        this.$core.utils.isNullOrUndefined(this.instanceOfAbstractDemand.preorderShipmentPeriod) === false
        && this.instanceOfAbstractDemand.preorderShipmentPeriod.isFilled() === true
      ) {
        return `Желаемый период отгрузки: ${this.instanceOfAbstractDemand.preorderShipmentPeriod}`;
      }
      else if (this.$core.utils.isNullOrUndefined(this.instanceOfAbstractDemand.desiredReceiptDate) === false) {
        return `Желаемая дата поставки: ${this.instanceOfAbstractDemand.desiredReceiptDate}`;
      }
      else {
        return null;
      }
    },

    /**
     * Расхождение по интервалу отгрузки. Сверяем в том же порядке, что и
     * подпись: сначала с периодом отгрузки образца, потом с желаемой датой
     * получения. Незаполненный интервал не сверяем — сверять нечего.
     */
    shipmentPeriodMismatch(): string | null {
      if (this.instanceOfAbstractDemand === null) {
        return null;
      }
      else if (this.localOffer.shipmentPeriod.isFilled() === false) {
        return null;
      }
      else if (
        this.$core.utils.isNullOrUndefined(this.instanceOfAbstractDemand.preorderShipmentPeriod) === false
        && this.instanceOfAbstractDemand.preorderShipmentPeriod.isFilled() === true
      ) {
        return this.localOffer.getPreorderShipmentPeriodMismatch(this.instanceOfAbstractDemand.preorderShipmentPeriod);
      }
      else if (this.$core.utils.isNullOrUndefined(this.instanceOfAbstractDemand.desiredReceiptDate) === false) {
        return this.localOffer.getReceiptDateMismatch(this.instanceOfAbstractDemand.desiredReceiptDate);
      }
      else {
        return null;
      }
    },

    desiredBuyerStore() {
      if (this.tender) {
        return this.tender.buyerStore.name;
      }

      if (this.demand) {
        return this.demand.buyerStore.name;
      }

      return 'г. Бердск, ул. Химзаводская, д. 11/90 своими силами и за свой счет.';
    },

    isPublicationPeriodTheSameShipmentPeriod: {
      get() {
        return (
          this.localOffer.publicationPeriod.start?.getTime() ===
          this.localOffer.shipmentPeriod.start?.getTime() &&
          this.localOffer.publicationPeriod.end?.getTime() ===
          this.localOffer.shipmentPeriod.end?.getTime()
        );
      },
      set(value) {
        this.localOffer.publicationPeriod = value
          ? this.$core.utils.cloneDeep(this.localOffer.shipmentPeriod)
          : new DateRange();
      }
    },

    publicationPeriodHelperText() {
      const {publicationPeriod} = this.localOffer;
      if (!this.publicationPeriodStart && !publicationPeriod.end) return null;
      let result = 'Предложение будет доступно для просмотра';
      if (publicationPeriod.start instanceof Date) {
        result += ` с ${this.$core.utils.date.format(this.publicationPeriodStart)}`;
      }
      if (publicationPeriod.end instanceof Date) {
        result += ` до ${this.$core.utils.date.format(publicationPeriod.end)}`;
      }
      return result;
    },

    publicationPeriodStart: {
      get() {
        if (this.isPublicationPeriodTheSameShipmentPeriod) {
          return this.$core.utils.date.toDayStart(new Date());
        }

        const start = this.localOffer?.publicationPeriod?.start;
        if (!start) return null;
        return this.$core.utils.date.isDate(start) ? start : this.$core.utils.date.toDate(start);
      },
      set(value) {
        if (
          !this.isPublicationPeriodTheSameShipmentPeriod
          && this.localOffer
          && this.localOffer.publicationPeriod
        ) {
          const asDate = this.$core.utils.date.toDate(value);
          this.localOffer.publicationPeriod.start = this.$core.utils.date.toDayStart(asDate);
        }
      }
    },

    /** Желаемые юр. лица — из действующих разрешений товара для этого поставщика. */
    desiredSellerCompanies(): Company[] {
      if (this.instanceOfAbstractDemand === null) {
        return [];
      }
      else {
        return this.instanceOfAbstractDemand.product.getDesiredSellerCompaniesByPartner(this.localOffer.seller);
      }
    },

    /** Желаемые производители — из разрешений товара, а при их отсутствии из самого товара. */
    desiredManufacturers(): Manufacturer[] {
      if (this.instanceOfAbstractDemand === null) {
        return [];
      }
      else {
        return this.instanceOfAbstractDemand.product.getDesiredManufacturersByPartner(this.localOffer.seller);
      }
    },

    /** Подпись для производителя. null — желаемых значений нет, показывать нечего. */
    desiredManufacturersLabel(): string | null {
      if (this.$core.utils.isEmptyArray(this.desiredManufacturers) === true) {
        return null;
      }
      else {
        return this.desired(
          'Желаемые производители',
          this.desiredManufacturers.map(_manufacturer => _manufacturer.name).join('\n'),
        );
      }
    },

    /** Подпись для юр. лица. null — желаемых значений нет, показывать нечего. */
    desiredSellerCompaniesLabel(): string | null {
      if (this.$core.utils.isEmptyArray(this.desiredSellerCompanies) === true) {
        return null;
      }
      else {
        return this.desired(
          'Желаемые юр. лица',
          this.desiredSellerCompanies.map(_company => _company.name).join('\n'),
        );
      }
    },


    isVatIncludedSeller: {
      set({value}) {
        this.localOffer.isVatIncludedSeller = value;
      },
      get() {
        return this.$core.dictionaries.vatInclusionOptions.find(_size => _size.value === this.localOffer.isVatIncludedSeller);
      },
    },

    currencyCode: {
      set({code}) {
        this.localOffer.offerCurrencyCode = code;
      },
      get() {
        return this.$core.utils.money.getCurrencyByCode(this.localOffer.offerCurrencyCode);
      },
    },

    /** Причина блокировки сабмита. null — блокировки нет. */
    submitDisabled(): string | null {
      if (this.sellerAgreementModel === false) {
        return 'Необходимо принять правила заключения договоров поставки';
      }

      return null;
    },

    /** Характеристики, доступные поставщику в форме
     *
     * не выводим пока новые характеристики "ссылка", "ссылка на продукт" и "ссылка на сторонний ресурс"
     * */
    visibleCharacteristics(): OfferCharacteristic[] {
      if (this.$core.utils.isNullOrUndefined(this.localOffer)) {
        return [];
      }
      else {
        return this.localOffer.characteristics.filter(_characteristic => {
          if (_characteristic.isEntityLink === true) {
            return false;
          }
          else if (_characteristic.isLink === true) {
            return false;
          }
          else {
            return true;
          }
        });
      }
    },

  },

  methods: {
    async init() {
      const offerId = this.offer?.id ?? this.id;

      if (offerId) {
        this.localOffer = await OffersService.get(offerId);
      } else if (this.offer) {
        this.localOffer = this.offer;
      } else {
        /** Категорию ставит источник селекта — из categoryId, тендера
         *  или потребности. */
        this.localOffer = Offer.fromNew();

        // Нужно проинициализировать правильного покупателя
        if (this.tender) {
          this.localOffer.setTender(this.tender);
        }
      }
      if(this.localOffer.isExisting) {
        await this.localOffer.calculateDeliveryPricePerUnit();
      }
    },

    /** Подпись «label: value [unit]» или null. Гейт наличия источника — на стороне вызова. */
    desired(label: string, value: unknown, unit: string | null = null): string | null {
      if (this.$core.utils.isNullOrUndefined(value) || (value instanceof Numeric && value.isEmpty()) || value === '') {
        return null;
      }

      if (unit === null) {
        return `${label}: ${value}`;
      } else {
        return `${label}: ${value} ${unit}`;
      }
    },


    /** TODO Нужно разделить
     * Валидация даты на прошедшую — общая для срока годности, интервалов отгрузки и публикации.
     *  Вызывается в двух режимах:
     *  - с одной Date (из isDisabled календаря — для каждого дня)
     *  - с {dateFrom, dateTo} (из Validation.validate — для всего диапазона)
     */
    validateDate(value) {
      const today = new DateSimple();

      // Режим "весь диапазон" — из Validation.validate({dateFrom, dateTo})
      if (value !== null && typeof value === 'object' && ('dateFrom' in value || 'dateTo' in value)) {

        const { dateFrom, dateTo } = value;

        if (dateFrom !== null && new DateSimple(dateFrom).isBefore(today)) {
          return 'Нельзя выбрать прошедшую дату начала периода';
        }

        if (dateTo === null) {
          return 'Укажите конечную дату периода';
        }

        return null;
      }

      // Режим "одна дата" — из isDisabled(date) календаря
      const selected = new DateSimple(value);
      if (selected.isBefore(today)) {
        return 'Нельзя выбрать прошедшую дату';
      }

      return null;
    },

    validatePackageVolume(value:Qty) {
      if(this.$core.utils.isNullOrUndefined(this.localOffer?.qtyAvailable) === false && value.greaterThan(this.localOffer.qtyAvailable)) {
        return 'Фасовка не может превышать указанное количество товара';
      }

      return null;
    },

    validateQty(value: Qty | null): string | null {
      ['preorderMinQtyInput', 'preorderMonthlyReserveInput'].forEach(key => {
        const input = this.$refs[key];
        if (input && typeof input.validate === 'function') {
          input.validate();
        }
      });

      return this.$core.utils.number.validateDesiredQty(
        value,
        this.localOffer.packageVolume,
        undefined,
        undefined,
        this.localOffer.unit,
      );
    },

    /**
     * Период публикации следует за периодом отгрузки, пока стоит галка.
     * Период получения пересчитывает сама модель — сеттер shipmentPeriod.
     */
    onInputShipmentPeriod() {
      if (this.isPublicationPeriodTheSameShipmentPeriod) {
        this.localOffer.publicationPeriod = this.localOffer.shipmentPeriod;
      }
    },

    //по просьбе Дмитрия, если фасовка в кг, то вес = фасовке
    setWeight(packageVolume: Qty) {
      if (this.localOffer?.unit?.name === 'кг') {
        this.localOffer.packageWeight = Weight.fromOperation(packageVolume?.value!)
      }
    },

    async toggleCategoryBookmark() {
      const previousState = this.localOffer.productCategory?.isBookmarked;
      const toggleAction = this.localOffer.productCategory?.isBookmarked ? 'delete' : 'add';

      const success = await BookmarksService[toggleAction](this.localOffer.productCategory?.id);

      if (success) { this.localOffer.productCategory.isBookmarked = !previousState; }
    },

    onSubmitButtonClicked() {
      this.$refs?.dialog?.onSubmitButtonClicked()
    },

    async onSubmit() {
      /** Товар известен только когда офер правят из потребности или тендера.
       *  Без образца связку поставщик + юр. лицо + производитель сверять
       *  не с чем — проверка пропускается. */
      let desiredProduct: Product | null;

      if (this.instanceOfAbstractDemand === null) {
        desiredProduct = null;
      }
      else {
        desiredProduct = this.instanceOfAbstractDemand.product;
      }

      const errors = this.localOffer.validate(desiredProduct);

      if (errors.length > 0) {
        this.$core.showNotification.error(errors.join('\n'));
      }
      else {
        this.isLoading = true;

        const success = this.localOffer.id
          ? await OffersService.update(this.localOffer)
          : await OffersService.add(this.localOffer);

        this.isLoading = false;

        if (success) {
          ContractsPublicService.addContractIntoLocalStorage('seller');

          /** Актуализируем согласие с договором продавца на беке — версия запрашивается в момент акцепта */
          const sellerContract = await ContractsPublicService.getActualContract('seller');
          await ContractsPublicService.accept([sellerContract]);

          this.$core.dialogs.OfferEditDialog.resolve();
        }
      }
    },

    onAddDoubleOffer() {
      this.localOffer = this.$core.utils.cloneDeep(this.localOffer);
      this.localOffer.id = null;
      this.localOffer.version = null;

      OffersService.showOfferEditDialog({offer: this.localOffer});
    },

    /** Открывает актуальную версию публичного договора.
     * Чтобы отправить к самой актуальной версии, нужно сначала узнать её айди */
    async openContract(contractType: string) {
      const contractsPublic = await ContractsPublicService.getActualContract(contractType);

      const route = this.$router.resolve(`${ContractsPublicService.baseUrl}/${contractType}/${contractsPublic.version}`);
      window.open(route.href, '_blank');
    },

  },
});
</script>
